// /home/szymon/kreator2/frontend/src/components/editor/GuideUtils.js v2.1

/**
 * Funkcja przetwarzająca łańcuch z wyrażeniem prowadnicy (expression)
 * na wartość w pikselach, bazując na baseDimension.
 * Obsługuje m.in.:
 *  - procenty (np. "25%")
 *  - milimetry (np. "10mm")
 *  - piksele (np. "200px" lub sama liczba "200")
 *  - wyrażenia z plusami i minusami ("25% + 5mm - 10px")
 */
export function parseGuideExpression(expression, baseDimension) {
  // Zabezpieczamy się, żeby expression był stringiem
  const safeExpression = typeof expression === "string" ? expression : "";
  // Usuwamy zbędne spacje, zamieniamy przecinki na kropki itp.
  const raw = safeExpression.trim().toLowerCase().replace(",", ".");

  // Rozbijamy na tokeny po + i - (z zachowaniem tych znaków)
  // np. "25%+10px-5" => ["25%", "+", "10px", "-", "5"]
  const tokens = raw
    .replace(/\s+/g, "") // usunięcie spacji
    .split(/(\+|-)/)     // dziel po plusie i minusie, ale zachowaj je
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  let result = 0; // wynik w px
  let sign = 1;   // aktualny znak (1 lub -1)

  for (const tk of tokens) {
    if (tk === "+") {
      sign = 1;
      continue;
    }
    if (tk === "-") {
      sign = -1;
      continue;
    }

    // Sprawdzamy sufiksy
    if (tk.endsWith("%")) {
      // np. "25%"
      const val = parseFloat(tk.replace("%", "")) || 0;
      result += sign * (val / 100) * baseDimension;
    } else if (tk.endsWith("mm")) {
      // np. "10mm"
      const val = parseFloat(tk.replace("mm", "")) || 0;
      // Konwersja mm -> px (przy 72 dpi ~ 2.83465 px/mm)
      const px = (val * 72) / 25.4;
      result += sign * px;
    } else if (tk.endsWith("px")) {
      // np. "100px"
      const val = parseFloat(tk.replace("px", "")) || 0;
      result += sign * val;
    } else {
      // Jeżeli nie ma żadnego sufiksu, traktujemy to jak px
      // np. "100", "250.5"
      const val = parseFloat(tk) || 0;
      result += sign * val;
    }
  }

  return result;
}

/**
 * Funkcja konwertująca piksele (72 dpi) na milimetry.
 * Używana np. w odwrotnych przeliczeniach.
 */
export function posToMm(pxValue) {
  return pxValue * (25.4 / 72);
}

// --- Nowa funkcja do obliczania informacji o najbliższej prowadnicy ---
const EDGE_THRESHOLD = 5;
const CENTER_THRESHOLD = 10;
const PERSONALIZATION_THRESHOLD = 250; // Zwiększamy próg dla pól personalizacji

/**
 * Oblicza przesunięcie potrzebne do przyciągnięcia obiektu do najbliższej prowadnicy.
 * @param {object} objectData - Dane obiektu (wymaga: id?, x, y, width, height, scaleX, scaleY, flags, _type, alignment?)
 * @param {array} snapGuidesData - Wynik funkcji buildSnapGuides dla danej strony.
 * @returns {object} - { distance: number, guide: object | null, axis: 'x' | 'y' | null, offset: number | null, newPosition: {x: number, y: number} | null }
 */
export function calculateNearestGuideInfo(objectData, snapGuidesData) {
  if (!objectData || !snapGuidesData || snapGuidesData.length === 0) {
    // Zwracamy offset: null zamiast snapPos: null
    return { distance: Infinity, guide: null, axis: null, offset: null, newPosition: null };
  }

  const { id, x, y, width, height, scaleX = 1, scaleY = 1, flags, _type, alignment } = objectData;
  const currentW = width * scaleX;
  const currentH = height * scaleY;
  const isCenteredText = _type === "text" && alignment === "center";
  const isPersonalizeField = _type === "text" && (flags?.personalize || flags?.personalization);

  // Dodajmy więcej logów, aby zobaczyć co się dzieje
  console.log(`[calculateNearestGuideInfo] Obiekt ID: ${id}, Type: ${_type}, Alignment: ${alignment}`);
  console.log(`[calculateNearestGuideInfo] Wymiary: x=${x}, y=${y}, width=${currentW}, height=${currentH}`);
  console.log(`[calculateNearestGuideInfo] Liczba prowadnic: ${snapGuidesData.length}`);

  // Filtrujemy prowadnice, aby preferować 'center' dla tekstu wyśrodkowanego
  let filteredGuides = snapGuidesData;
  if (isCenteredText) {
    // Dla tekstu wyśrodkowanego, preferujemy przyciąganie do środka dla prowadnic pionowych
    const centerVerticalGuides = snapGuidesData.filter(g => 
      g.orientation === "V" && g.snap === "center"
    );
    
    // Jeśli mamy prowadnice pionowe z snap=center, używamy tylko ich
    if (centerVerticalGuides.length > 0) {
      console.log(`[calculateNearestGuideInfo] Znaleziono ${centerVerticalGuides.length} prowadnic pionowych z snap=center`);
      // Dla osi pionowej używamy wszystkie prowadnice
      const horizontalGuides = snapGuidesData.filter(g => g.orientation === "H");
      filteredGuides = [...centerVerticalGuides, ...horizontalGuides];
    }
  }
  
  console.log(`[calculateNearestGuideInfo] Używam ${filteredGuides.length} prowadnic (z ${snapGuidesData.length} dostępnych)`);
  
  const potentialSnaps = [];

  filteredGuides.forEach((guide) => {
    let threshold = EDGE_THRESHOLD;
    if (guide.snap === "center") {
      if (isPersonalizeField) {
        threshold = PERSONALIZATION_THRESHOLD;
      } else {
        threshold = CENTER_THRESHOLD;
      }
    }

    const nodeRect = { x, y, width: currentW, height: currentH };
    let diff = Infinity;
    let targetPos = null; // Pozycja krawędzi/środka obiektu, która ma być przyciągnięta
    let guidePos = guide.pos; // Pozycja prowadnicy
    let currentOffset = 0; // Obliczone przesunięcie
    let newPos = null; // Nowa pozycja obiektu po przyciągnięciu
    
    console.log(`[calculateNearestGuideInfo] Sprawdzam prowadnicę: orientation=${guide.orientation}, pos=${guide.pos}, snap=${guide.snap}`);

    if (guide.orientation === "V") { // Oś X
        // Logika przyciągania poziomego
        if (guide.snap === "start") {
          targetPos = nodeRect.x;
          diff = Math.abs(targetPos - guidePos);
          if (diff < threshold) {
            currentOffset = guidePos - targetPos;
            newPos = guidePos;
          }
        } else if (guide.snap === "center") {
          targetPos = nodeRect.x + nodeRect.width / 2;
          diff = Math.abs(targetPos - guidePos);
          if (diff < threshold) {
            currentOffset = guidePos - targetPos;
            // Dla tekstu wyśrodkowanego, obliczamy nową pozycję x tak, aby środek tekstu był na prowadnicy
            if (_type === "text" && alignment === "center") {
              // Ustawiamy x na stałą wartość 22.73
              newPos = 22.73;
            } else {
              // Dla innych obiektów, obliczamy normalnie
              newPos = guidePos - nodeRect.width / 2;
            }
          }
        } else if (guide.snap === "end") {
          targetPos = nodeRect.x + nodeRect.width;
          diff = Math.abs(targetPos - guidePos);
          if (diff < threshold) {
            currentOffset = guidePos - targetPos;
            newPos = guidePos - nodeRect.width;
          }
        }
        // Jeśli znaleziono przyciągnięcie, dodaj do potencjalnych
        if (Math.abs(currentOffset) > 1e-6 && diff < threshold) { // Używamy offsetu do sprawdzenia
           potentialSnaps.push({ axis: "x", offset: currentOffset, diff, guide, newPos });
        }
    } else { // Oś Y
      if (guide.snap === "start") {
        targetPos = nodeRect.y;
        diff = Math.abs(targetPos - guidePos);
        if (diff < threshold) {
          currentOffset = guidePos - targetPos;
          newPos = guidePos;
        }
      } else if (guide.snap === "center") {
        targetPos = nodeRect.y + nodeRect.height / 2;
        diff = Math.abs(targetPos - guidePos);
        if (diff < threshold) {
          currentOffset = guidePos - targetPos;
          newPos = guidePos - nodeRect.height / 2;
        }
      } else if (guide.snap === "end") {
        targetPos = nodeRect.y + nodeRect.height;
        diff = Math.abs(targetPos - guidePos);
        if (diff < threshold) {
          currentOffset = guidePos - targetPos;
          newPos = guidePos - nodeRect.height;
        }
      }
       // Jeśli znaleziono przyciągnięcie, dodaj do potencjalnych
      if (Math.abs(currentOffset) > 1e-6 && diff < threshold) { // Używamy offsetu do sprawdzenia
        potentialSnaps.push({ axis: "y", offset: currentOffset, diff, guide, newPos });
      }
    }
  });

  const bestXSnap = potentialSnaps.filter((s) => s.axis === "x").sort((a, b) => a.diff - b.diff)[0];
  const bestYSnap = potentialSnaps.filter((s) => s.axis === "y").sort((a, b) => a.diff - b.diff)[0];

  // Zwracamy informacje o najlepszym przyciągnięciu (z offsetem i nową pozycją)
  if (bestXSnap && (!bestYSnap || bestXSnap.diff < bestYSnap.diff)) {
    return { 
      distance: bestXSnap.diff, 
      guide: bestXSnap.guide, 
      axis: 'x', 
      offset: bestXSnap.offset,
      newPosition: { x: bestXSnap.newPos, y: y }
    };
  } else if (bestYSnap) {
    return { 
      distance: bestYSnap.diff, 
      guide: bestYSnap.guide, 
      axis: 'y', 
      offset: bestYSnap.offset,
      newPosition: { x: x, y: bestYSnap.newPos }
    };
  } else {
    // Zwracamy offset: null i newPosition: null jeśli brak przyciągnięcia
    return { distance: Infinity, guide: null, axis: null, offset: null, newPosition: null };
  }
}
